/**
 * "Email update" on a change-log entry (docs/SYSTEM.md → Emailing a change,
 * D-016): a short, branded note to chosen people in the app, with the
 * change's video as its picture.
 *
 * Brevity is the point — a subject of about 50 characters and a body of a
 * sentence or three. The full story stays on System → Change.
 */
import { z } from 'zod'

/** A subject longer than this gets cut in most inboxes' lists. */
export const CHANGE_EMAIL_SUBJECT_MAX = 50
/** Per send, and per Admin per window — an update, not a mailing list. */
export const CHANGE_EMAIL_MAX_RECIPIENTS = 50
export const CHANGE_EMAIL_SENDS_PER_WINDOW = 5
export const CHANGE_EMAIL_WINDOW_MS = 10 * 60_000

/** `**bold**`, `` `code` `` and `[links](…)` read as plain words in a subject or a short note. */
function plain(markdown: string): string {
  return markdown
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Cut at a word, never mid-word, with an ellipsis when anything was dropped. */
function shorten(text: string, max: number): string {
  if (text.length <= max) return text
  const cut = text.slice(0, max - 1)
  const atWord = cut.slice(0, Math.max(cut.lastIndexOf(' '), 0)).replace(/[\s,;:—–-]+$/, '')
  return `${atWord || cut}…`
}

/**
 * The prefilled subject and body: "New: " and the title up to its first
 * clause, then the opening sentence or two of the summary. Both are starting
 * points — the Admin edits them in the dialog.
 */
export function defaultChangeEmail(entry: { title: string; summary: string }): {
  subject: string
  body: string
} {
  const headline = plain(entry.title).split(/\s+[—–]\s+|;\s+|,\s+(?:and|with)\s+/)[0] ?? ''
  const subject = shorten(`New: ${headline}`, CHANGE_EMAIL_SUBJECT_MAX)
  const sentences = plain(entry.summary).match(/[^.!?]+[.!?]+/g) ?? [plain(entry.summary)]
  // One sentence, or two when the first is short — a note, not the change log.
  let body = ''
  for (const sentence of sentences.slice(0, 2)) {
    const next = `${body} ${sentence.trim()}`.trim()
    if (body && next.length > 240) break
    body = next
  }
  return { subject, body: shorten(body, 400) }
}

export const changeEmailDraftSchema = z.object({
  slug: z.string().regex(/^\d+-[a-z0-9-]+$/),
  subject: z
    .string()
    .trim()
    .min(1, 'Add a subject.')
    .max(120)
    .refine((text) => !/[\r\n]/.test(text), 'A subject is one line.'),
  body: z.string().trim().min(1, 'Add a short note.').max(2_000),
})

export const sendChangeEmailInputSchema = changeEmailDraftSchema.extend({
  /** Members of this workspace, by their Users-page id. */
  to: z
    .array(z.string().min(1))
    .min(1, 'Add someone to send it to.')
    .max(CHANGE_EMAIL_MAX_RECIPIENTS),
})
export type SendChangeEmailInput = z.infer<typeof sendChangeEmailInputSchema>

/** Who can receive one: an active member, not someone still waiting for access. */
export function canReceiveChangeEmail(user: { status: string; role: string }): boolean {
  return user.status === 'active' && user.role !== 'requested'
}
